import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { currentHeaderHeightPx } from 'src/components/layout/redesign/header-shared/header-styling';

import throttleToAnimationFrames from '../helpers/throttle-to-animation-frames';

const SCROLL_BELOW_HEADER_BEFORE_HIDE_PX = 100;

const subscribeToWindowScroll = (callback: () => void): (() => void) => {
  window.addEventListener('scroll', callback, { passive: true });
  return () => window.removeEventListener('scroll', callback);
};

const getShouldHideFromScroll = (): boolean =>
  window.scrollY - currentHeaderHeightPx() > SCROLL_BELOW_HEADER_BEFORE_HIDE_PX;

const getServerShouldHide = () => false;

export const useRibbonEffects = (defaultHeight = 56, isDomReady = true) => {
  const [shouldShowExpand, setShowExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasRecentlyChangedShouldHide, setHasRecentlyChangedShouldHide] = useState(false);
  const [componentHeight, setComponentHeight] = useState(defaultHeight);

  const componentRef = useRef<HTMLDivElement | null>(null);
  const textAndChevronContainerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const chevronRef = useRef<HTMLDivElement | null>(null);

  const liveShouldHide = useSyncExternalStore(
    subscribeToWindowScroll,
    getShouldHideFromScroll,
    getServerShouldHide
  );

  const [shouldHide, setShouldHide] = useState(false);
  if (liveShouldHide !== shouldHide && !hasRecentlyChangedShouldHide) {
    setShouldHide(liveShouldHide);
    setHasRecentlyChangedShouldHide(true);
  }

  useEffect(() => {
    if (!hasRecentlyChangedShouldHide) {
      return;
    }
    const timeoutId = window.setTimeout(() => setHasRecentlyChangedShouldHide(false), 400);
    return () => clearTimeout(timeoutId);
  }, [hasRecentlyChangedShouldHide]);

  const updateShouldShowExpand = useCallback(() => {
    if (!textRef.current || !textAndChevronContainerRef.current) {
      return;
    }

    if (!isExpanded) {
      setShowExpand(textRef.current.scrollWidth >= textAndChevronContainerRef.current.offsetWidth);
      return;
    }

    if (!chevronRef.current) {
      return;
    }

    const isTextOnOneLine = textRef.current.scrollHeight <= chevronRef.current.scrollHeight;

    if (isTextOnOneLine) {
      setShowExpand(false);
      setIsExpanded(false);
    }
  }, [isExpanded]);

  const updateComponentHeight = useCallback(() => {
    if (!isDomReady) {
      return;
    }
    setComponentHeight(componentRef.current?.offsetHeight || defaultHeight);
  }, [defaultHeight, isDomReady]);

  useLayoutEffect(() => {
    updateShouldShowExpand();
  }, [updateShouldShowExpand]);

  useEffect(() => {
    if (!isDomReady) {
      return;
    }
    const componentElement = componentRef.current;
    const textAndChevronElement = textAndChevronContainerRef.current;
    if (!componentElement && !textAndChevronElement) return;

    const { throttled: onResize, cancel: cancelPendingResize } = throttleToAnimationFrames(() => {
      updateShouldShowExpand();
      updateComponentHeight();
    });
    const resizeObserver = new ResizeObserver(onResize);
    if (componentElement) resizeObserver.observe(componentElement);
    if (textAndChevronElement) resizeObserver.observe(textAndChevronElement);
    return () => {
      resizeObserver.disconnect();
      cancelPendingResize();
    };
  }, [isDomReady, updateComponentHeight, updateShouldShowExpand]);

  return {
    shouldShowExpand,
    componentHeight,
    updateComponentHeight,
    isExpanded,
    shouldHide,
    setIsExpanded,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  };
};
